import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import SearchableSelect from './SearchableSelect';
import geolocationService from '../services/geolocationService';

const PhoneInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const CountryCodeWrapper = styled.div`
  min-width: 140px;
  max-width: 140px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-width: 110px;
    max-width: 110px;
  }

  @media (max-width: 480px) {
    min-width: 95px;
    max-width: 95px;
  }
`;

const PhoneNumberInput = styled.input`
  flex: 1;
  min-width: 0; /* Allow flex item to shrink below content size */
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.inputBorder};
  border-radius: 8px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};

  &:focus {
    border-color: ${props => props.theme.inputFocus};
    outline: none;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }

  @media (max-width: 768px) {
    padding: 0.65rem;
    font-size: 0.95rem;
  }
`;

const ErrorText = styled.span`
  color: ${props => props.theme.error};
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

const COUNTRIES: Country[] = [
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
  { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  defaultCountry?: string; // Default country code (e.g., 'PH')
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  onChange,
  onBlur,
  disabled = false,
  error,
  placeholder = 'Enter phone number',
  defaultCountry = 'PH'
}) => {
  const [countryCode, setCountryCode] = useState<string>('+63');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isDetecting, setIsDetecting] = useState<boolean>(false);

  // Auto-detect country on mount (only if no value provided)
  useEffect(() => {
    const detectCountry = async () => {
      if (!value) {
        setIsDetecting(true);
        try {
          const detectedCountryCode = await geolocationService.getUserCountryCode();
          const detectedCountry = COUNTRIES.find(c => c.code === detectedCountryCode);

          if (detectedCountry) {
            setCountryCode(detectedCountry.dialCode);
            console.log('✅ Auto-detected country:', detectedCountry.name, detectedCountry.dialCode);
          } else {
            // Fallback to default
            const defaultCtry = COUNTRIES.find(c => c.code === defaultCountry);
            if (defaultCtry) {
              setCountryCode(defaultCtry.dialCode);
            }
          }
        } catch (error) {
          console.warn('Country detection failed, using default');
          const defaultCtry = COUNTRIES.find(c => c.code === defaultCountry);
          if (defaultCtry) {
            setCountryCode(defaultCtry.dialCode);
          }
        } finally {
          setIsDetecting(false);
        }
      }
    };

    detectCountry();
  }, []); // Run only once on mount

  // Initialize from value prop (when editing existing data)
  useEffect(() => {
    if (value) {
      // Check if value already has country code
      const matchedCountry = COUNTRIES.find(c => value.startsWith(c.dialCode));

      if (matchedCountry) {
        setCountryCode(matchedCountry.dialCode);
        setPhoneNumber(value.substring(matchedCountry.dialCode.length));
      } else if (value.startsWith('+')) {
        // Has + but not in our list, extract code
        const codeMatch = value.match(/^\+(\d{1,4})/);
        if (codeMatch) {
          const extractedCode = '+' + codeMatch[1];
          setCountryCode(extractedCode);
          setPhoneNumber(value.substring(extractedCode.length));
        } else {
          setPhoneNumber(value);
        }
      } else {
        // No country code, just the number
        setPhoneNumber(value);
      }
    }
  }, [value]);

  const handleCountryCodeChange = (newCode: string) => {
    setCountryCode(newCode);
    // Update combined value
    const combined = phoneNumber ? `${newCode}${phoneNumber}` : newCode;
    onChange(combined);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers
    let input = e.target.value.replace(/[^\d]/g, '');

    // If the user typed/pasted the country code prefix into the number box
    // (e.g. "+631234567890" or "631234567890"), strip those leading digits —
    // the dropdown already supplies the country code so we don't want it doubled.
    const dialDigits = countryCode.replace('+', '');
    if (input.startsWith(dialDigits) && input.length > dialDigits.length) {
      input = input.substring(dialDigits.length);
    }

    setPhoneNumber(input);

    // Update combined value
    const combined = input ? `${countryCode}${input}` : '';
    onChange(combined);
  };

  const handlePhoneNumberBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  // Convert countries to options format for SearchableSelect
  const countryOptions = COUNTRIES.map(country => ({
    text: `${country.flag} ${country.dialCode} ${country.name}`,
    value: country.dialCode
  }));

  return (
    <div>
      <PhoneInputContainer>
        <CountryCodeWrapper>
          <SearchableSelect
            options={countryOptions}
            value={countryCode}
            onChange={(value: string) => handleCountryCodeChange(value)}
            placeholder="Country"
            disabled={disabled}
          />
        </CountryCodeWrapper>

        <PhoneNumberInput
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          onBlur={handlePhoneNumberBlur}
          disabled={disabled}
          placeholder={placeholder}
          inputMode="numeric"
        />
      </PhoneInputContainer>

      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
};

export default PhoneInput;
